'use client';

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronDown } from 'lucide-react';
import { locales, type Locale } from '@/lib/content';
import { cn } from '@/lib/utils';
import { toolTranslations } from '@/lib/tools/tools-config';
import { blogTranslations } from '@/lib/blog-translations';
import { pageTranslations } from '@/lib/page-translations';

interface LanguageSwitcherProps {
  currentLocale: Locale;
  className?: string;
  variant?: 'dropdown' | 'list';
}

const languageLabels: Record<Locale, string> = {
  nl: 'NL',
  en: 'EN',
  de: 'DE',
  fr: 'FR',
};

export function LanguageSwitcher({ currentLocale, className, variant = 'dropdown' }: LanguageSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const pathname = usePathname();
  const labelId = useId();

  const normalizedPath = useMemo(() => {
    if (!pathname || pathname === '/') {
      return [] as string[];
    }
    const trimmed = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
    return trimmed.split('/').filter(Boolean);
  }, [pathname]);

  const buildHref = (targetLocale: Locale) => {
    if (normalizedPath.length === 0) {
      return `/${targetLocale}/`;
    }
    const segments = [...normalizedPath];
    segments[0] = targetLocale;

    // Handle tools page slug translation
    if (segments.length >= 3 && segments[1] === 'tools') {
      const currentSlug = segments[2];
      const slugTranslations = toolTranslations[currentSlug];
      if (slugTranslations && slugTranslations[targetLocale]) {
        segments[2] = slugTranslations[targetLocale];
      }
    }

    // Handle blog page slug translation
    if (segments.length >= 3 && segments[1] === 'blog') {
      const currentSlug = segments[2];
      const slugTranslations = blogTranslations[currentSlug];
      if (slugTranslations && slugTranslations[targetLocale]) {
        segments[2] = slugTranslations[targetLocale];
      } else {
        // No translation available — redirect to blog overview
        return `/${targetLocale}/blog/`;
      }
    }

    // Handle content page slug translation (e.g., /fr/plateformes/ → /de/plattformen/)
    if (segments.length === 2) {
      const currentSlug = segments[1];
      const slugTranslations = pageTranslations[currentSlug];
      if (slugTranslations && slugTranslations[targetLocale]) {
        segments[1] = slugTranslations[targetLocale];
      } else if (!slugTranslations) {
        // No translation available — redirect to homepage
        return `/${targetLocale}/`;
      }
    }

    return `/${segments.join('/')}/`;
  };

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) && !triggerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [currentLocale]);

  const otherLocales = locales.filter((locale) => locale !== currentLocale);

  if (variant === 'list') {
    return (
      <div className={cn('flex flex-wrap gap-2', className)}>
        {locales.map((locale) => (
          <Link
            key={locale}
            href={buildHref(locale)}
            hrefLang={locale}
            lang={locale}
            className={cn(
              'rounded-lg px-3 py-2 text-sm font-medium transition',
              currentLocale === locale
                ? 'bg-brand-100 text-brand-700'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            )}
          >
            {languageLabels[locale]}
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('relative inline-flex', className)}>
      <button
        ref={triggerRef}
        type="button"
        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-sm font-medium text-slate-700 shadow-sm transition hover:border-brand-400 hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`${currentLocale.toUpperCase()} – taal selecteren`}
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsOpen(true);
            const firstLink = menuRef.current?.querySelector<HTMLAnchorElement>('a');
            firstLink?.focus();
          }
        }}
      >
        <span>{languageLabels[currentLocale]}</span>
        <ChevronDown className={cn('h-4 w-4 text-slate-400 transition-transform', isOpen && 'rotate-180')} aria-hidden="true" />
      </button>
      <div
        ref={menuRef}
        role="listbox"
        aria-label="Beschikbare talen"
        className={cn(
          'absolute right-0 top-full z-50 mt-2 min-w-[140px] rounded-xl border border-slate-100 bg-white p-1 shadow-lg transition-opacity',
          isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        )}
      >
        <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600" id={labelId}>Taal</div>
        <div role="group" aria-labelledby={labelId}>
          <div role="option" aria-selected="true" className="block rounded-lg bg-brand-50 px-3 py-2 text-sm font-medium text-brand-700 my-1">
            {languageLabels[currentLocale]}
          </div>
          {otherLocales.map((locale) => (
            <Link
              key={locale}
              role="option"
              aria-selected="false"
              href={buildHref(locale)}
              hrefLang={locale}
              lang={locale}
              className="block rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-brand-50 hover:text-brand-700 focus:bg-brand-50 focus:text-brand-700 focus:outline-none my-1"
              onClick={() => setIsOpen(false)}
            >
              {languageLabels[locale]}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
